import React, { useContext, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useSelector, useDispatch } from 'react-redux'
import styled from 'styled-components'

import cloneDeep from 'lodash/cloneDeep'
import get from 'lodash/get'
import includes from 'lodash/includes'

import CircularProgress from '@eig-builder/core-ui/CircularProgress'
import Checkbox from '@eig-builder/core-ui/Checkbox'
import Select from '@eig-builder/control-select'
import Text from '@eig-builder/module-localization'

import useFetchAsJson from '@eig-builder/core-utils/hooks/useFetchAsJson'
import { DataElementLocations } from '@eig-builder/core-utils/helpers/tagging-helper'
import { getCurrencySymbol } from '@eig-builder/core-utils/helpers/currency-helper'

import { RefundContext } from './context'

import { getCrmApiOptions } from '../../../helpers/fetch-options-helper'
import { getRefundOptions } from './helpers/get-refund-options'
import TimeFormat from '../../../helpers/time-format'

import { getRefundableServices } from './../../../store/actions'

import './lang'
import { DIRECTLY } from '../services-tab/constants'

const TotalRow = styled.div`
  font-weight: 600;
`

const TableRow = styled.div`
  font-weight: 600;
`

const LoaderWrapper = styled.div`
  text-align: center;
`

const UnpaidInvoiceText = styled.div`
  color: ${({ theme }) => theme.palette.warning.main};
`

const PullRight = styled.div`
  float: right;
`

const RefundInformation = ({ showCreditOption, refundServices, handleSkipId, skipDomainRegistrationFreePlanIds, cancelType }) => {
  const { refundType, setRefundType } = useContext(RefundContext)
  const refundOptions = getRefundOptions(showCreditOption)

  const dispatch = useDispatch()
  const account = useSelector(state => state.crm.getAccountByIdResponse)

  const [unpaidInvoicesReq, getUnpaidInvoices] = useFetchAsJson()

  const loading = useSelector(state => state.crm.getRefundableServicesRetrieving) || unpaidInvoicesReq.isLoading

  const [refundTotal, setRefundTotal] = useState(0)
  const [refundableServices, setRefundableServices] = useState([])
  const [copyRefundServices, setCopyRefundServices] = useState([])

  useEffect(() => {
    if (unpaidInvoicesReq.isLoading) {
      return
    }
    // Check for error state, since the backend gave an error which caused an endless loop here
    if (refundServices.length > 0 && !unpaidInvoicesReq.isSuccess && !unpaidInvoicesReq.isError) {
      const billingServiceIds = refundServices.map(s => s.billingServiceId).join(',')
      getUnpaidInvoices(getCrmApiOptions(account, `/v1.0/services/unpaid_invoices?billingServiceIdsString=${billingServiceIds}`))
    }
  }, [refundServices, unpaidInvoicesReq.isSuccess, unpaidInvoicesReq.isError, unpaidInvoicesReq.isLoading])

  useEffect(() => {
    if ((refundServices.length !== refundableServices.length) !== copyRefundServices.length && refundTotal === 0) {
      setRefundTotal(refundServices.reduce((acc, val) => acc + val.refundOrCreditAmount, 0))
      setRefundableServices(refundServices)
      setCopyRefundServices(cloneDeep(refundServices))
    }
  }, [loading])

  useEffect(() => {
    const { id: accountId, partnerId } = account
    dispatch(getRefundableServices(accountId, partnerId))
  }, [])

  const handleOnChangeCheckBox = plan => {
    let calcNewRefundTotal = 0
    if (plan.initialDomainRegistrationFee > 0) {
      calcNewRefundTotal = refundTotal + plan.initialDomainRegistrationFee
      plan.refundOrCreditAmount = plan.refundOrCreditAmount + plan.initialDomainRegistrationFee
      plan.initialDomainRegistrationFee = 0
    } else {
      const copyPlan = copyRefundServices && copyRefundServices.find(i => i.billingServiceId === plan.billingServiceId)
      calcNewRefundTotal = refundTotal - copyPlan.initialDomainRegistrationFee
      plan.refundOrCreditAmount = plan.refundOrCreditAmount - copyPlan.initialDomainRegistrationFee
      plan.initialDomainRegistrationFee = copyPlan.initialDomainRegistrationFee
    }
    handleSkipId(parseInt(plan.serviceId))
    setRefundTotal(calcNewRefundTotal)
  }

  if (loading) {
    return (
      <LoaderWrapper>
        <CircularProgress centered />
        <div className='d-inline-block mt-3'>
          Checking for refund options and/or open invoices...
        </div>
      </LoaderWrapper>
    )
  }

  if (unpaidInvoicesReq.isError) {
    return (
      <div className='d-inline-block mt-3'>
        Something went wrong loading the refund options. Please try again.
      </div>
    )
  }

  const unpaidInvoices = unpaidInvoicesReq.data || []

  const showRefundOptions = cancelType === DIRECTLY && refundableServices && refundableServices.length > 0

  return <>
    {unpaidInvoices &&
      unpaidInvoices.map(i => {
        const invoiceId = `INV-${account.uberClientId}-${i.invoice_id}`
        const service = refundServices.find(s => Number(s.billingServiceId) === i.uber_service_id)
        const serviceName = get(service, 'planName', 'unknown service')
        const amount = `${getCurrencySymbol(account.currencyCode)} ${i.amount_unpaid.toFixed(2)}`

        return <UnpaidInvoiceText>
          {`There's an open invoice ${invoiceId} for ${serviceName} that will be satisfied for an amount of ${amount}.`}
        </UnpaidInvoiceText>
      })
    }
    {showRefundOptions &&
      <div>
        <div>
          {showCreditOption ? (
            <Text message='modules.crm.components.account.refund.extendedDescription' />
          ) : (
            <Text message='modules.crm.components.account.refund.description' />
            )}
        </div>
        <div className='mt-3'>
          <TableRow className='row'>
            <div className='col-3'>
              <Text message='modules.crm.components.account.refund.tableRow.planName' />
            </div>
            <div className='col-6'>
              <Text message='modules.crm.components.account.refund.tableRow.renewDate' />
            </div>
            <div className='col-2 ml-auto mr-5'>
              <PullRight>
                <Text message='modules.crm.components.account.refund.tableRow.amount' />
              </PullRight>
            </div>
          </TableRow>
          {refundableServices.map(plan => {
            return (
              <div key={plan.billingServiceId}>
                <div className='row'>
                  <div className='col-3'>{plan.planName}</div>
                  <div className='col-6'>
                    <TimeFormat date={plan.renewDate} />
                  </div>
                  <div className='col-2'>
                    <PullRight>
                      {`${getCurrencySymbol(account.currencyCode)} ${plan.proRatedAmount.toFixed(2)}`}
                    </PullRight>
                  </div>
                </div>
                {copyRefundServices &&
                  copyRefundServices.find(i => i.billingServiceId === plan.billingServiceId)
                    .initialDomainRegistrationFee ? (
                      <div className='row'>
                        <div className='col-6'>
                          <Checkbox
                            checked={!includes(skipDomainRegistrationFreePlanIds, parseInt(plan.serviceId))}
                            onChange={() => handleOnChangeCheckBox(plan)}
                            value='includeInitialDomainRegistrationFee'
                            color='primary'
                            style={{ paddingLeft: 0, paddingTop: 0, paddingBottom: 0 }}
                            dataElementLocation={DataElementLocations.BODY}
                            dataElementLabel='cst-account--refund-information--incl-init-dom-reg-fee-checkbox'
                            dataElementId='cst-account--refund-information--incl-init-dom-reg-fee-checkbox'
                        />
                          <Text message='modules.crm.components.account.refund.initialDomainRegistrationFee' />
                        </div>
                        {plan.initialDomainRegistrationFee ? (
                          <div className='col-3 ml-auto mr-5'>
                            <PullRight>
                              {`${getCurrencySymbol(account.currencyCode)} -${plan.initialDomainRegistrationFee &&
                              plan.initialDomainRegistrationFee.toFixed(2)}`}
                            </PullRight>
                          </div>
                      ) : (
                          ''
                        )}
                      </div>
                  ) : (
                    ''
                  )}
              </div>
            )
          })}
          <TotalRow className='row'>
            <div className='col-3'>
              <Text message='modules.crm.components.account.refund.total' />
            </div>
            <div className='col-3 ml-auto mr-5'>
              <PullRight>{`${getCurrencySymbol(account.currencyCode)} ${refundTotal.toFixed(2)}`}</PullRight>
            </div>
          </TotalRow>
        </div>
        <div className='mt-4'>
          <Select
            variant='outlined'
            label='Refund type'
            options={refundOptions}
            value={refundType}
            onChange={value => setRefundType(value)}
            dataElementLocation={DataElementLocations.BODY}
            dataElementLabel='cst-account--refund-information--type-select'
            dataElementId='cst-account--refund-information--type-select'
          />
        </div>
      </div>}
  </>
}

RefundInformation.propTypes = {
  showCreditOption: PropTypes.bool.isRequired,
  refundServices: PropTypes.array.isRequired,
  handleSkipId: PropTypes.func.isRequired,
  skipDomainRegistrationFreePlanIds: PropTypes.array.isRequired,
  cancelType: PropTypes.string.isRequired
}

RefundInformation.defaultProps = {
  showCreditOption: false,
  refundServices: []
}

export default RefundInformation
